<script setup lang="ts">
/**
 * Segmented control for a genuinely exclusive choice — the focus-goal kind
 * (longer blocks vs more often), the Add-subject level filter.
 *
 * Uses radio semantics because exactly one option is always active. If the
 * choice is additive or a ranking, use `RlChip` instead.
 */

interface Option {
  value: string;
  label: string;
}

defineProps<{
  options: readonly Option[];
  modelValue: string;
  /** Group label for assistive tech; there is no visible heading. */
  label: string;
}>();

defineEmits<{ "update:modelValue": [value: string] }>();
</script>

<template>
  <div class="segmented" role="radiogroup" :aria-label="label">
    <button
      v-for="opt in options"
      :key="opt.value"
      type="button"
      role="radio"
      class="segment"
      :class="{ 'segment-on': opt.value === modelValue }"
      :aria-checked="opt.value === modelValue"
      @click="$emit('update:modelValue', opt.value)"
    >
      {{ opt.label }}
    </button>
  </div>
</template>

<style scoped>
.segmented {
  display: inline-flex;
  gap: 2px;
  padding: 2px;
  background: var(--rl-bg-muted);
  border-radius: var(--radius);
}

.segment {
  padding: var(--sp-1) var(--sp-3);
  font-family: var(--rl-font-sans);
  font-size: var(--rl-text-sm);
  font-weight: 500;
  color: var(--rl-fg-3);
  cursor: pointer;
  background: none;
  border: none;
  border-radius: var(--radius-sm);
  transition:
    background-color var(--t-fast),
    color var(--t-fast);
}

.segment:hover {
  color: var(--rl-fg-1);
}

.segment:focus-visible {
  outline: none;
  box-shadow: var(--shadow-focus);
}

.segment-on {
  color: var(--rl-fg-1);
  background: var(--rl-bg);
  box-shadow: var(--shadow-xs);
}
</style>
